import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import {
  preloadInfo,
  preloadMusicCatalog,
  preloadPhotos,
} from './lib/api';
import { presetCatalogRowHeight } from './hooks/useCatalogRowFit';
import { bootstrapTheme } from './hooks/usePreferences';
import { initMobileOrientation } from './lib/mobileOrientation';
import './styles/globals.css';
import './styles/layout.css';
import './styles/vaul-drawer.css';

bootstrapTheme();
initMobileOrientation();

function syncDisplayMode() {
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari Add to Home Screen
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
  document.documentElement.dataset.displayMode = standalone
    ? 'standalone'
    : 'browser';
  if (!standalone) {
    delete document.documentElement.dataset.pwaSplash;
    delete document.documentElement.dataset.pwaSplashAt;
  }
}

syncDisplayMode();
window
  .matchMedia('(display-mode: standalone)')
  .addEventListener('change', syncDisplayMode);

/**
 * PWA splash: stay up until boot data is ready, and never shorter than 3s
 * (reduced-motion: still wait on data, but allow a shorter floor).
 */
function dismissPwaSplash(dataReady: Promise<unknown>) {
  const root = document.documentElement;
  const splash = document.getElementById('pwa-splash');
  if (!splash || root.dataset.pwaSplash !== '1') {
    splash?.remove();
    delete root.dataset.pwaSplash;
    delete root.dataset.pwaSplashAt;
    return;
  }

  const startedAt = Number(root.dataset.pwaSplashAt) || Date.now();
  const minMs = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 400
    : 3000;

  const fadeOut = () => {
    splash.setAttribute('data-done', '');
    const remove = () => {
      splash.remove();
      delete root.dataset.pwaSplash;
      delete root.dataset.pwaSplashAt;
    };
    splash.addEventListener('transitionend', remove, { once: true });
    window.setTimeout(remove, 500);
  };

  void dataReady
    .catch(() => {
      /* still dismiss — app can retry in UI */
    })
    .then(() => {
      const wait = Math.max(0, minMs - (Date.now() - startedAt));
      // One painted frame after data settles so the catalog isn't blank under the fade.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.setTimeout(fadeOut, wait);
        });
      });
    });
}

// Row heights before first paint so catalog/skeleton don't jump.
presetCatalogRowHeight();
window.addEventListener('resize', presetCatalogRowHeight, { passive: true });

// Warm catalogs so music ↔ photo ↔ info mode slides never wait on refetch.
// Splash hide is gated on this same promise (plus a 3s minimum in standalone).
const bootData = Promise.all([
  preloadInfo(),
  preloadMusicCatalog(),
  preloadPhotos(),
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>,
);

dismissPwaSplash(bootData);

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void (async () => {
      try {
        // Drop accidental /graystore.js regs from the brief rename experiment.
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          regs.map(async (reg) => {
            const url =
              reg.active?.scriptURL ||
              reg.waiting?.scriptURL ||
              reg.installing?.scriptURL ||
              '';
            if (/\/graystore\.js(\?|$)/.test(url)) {
              await reg.unregister();
            }
          }),
        );
        await navigator.serviceWorker.register('/sw.js?v=1.0', {
          updateViaCache: 'none',
        });
      } catch {
        /* SW optional */
      }
    })();
  });
}
